import type {
  CarrierEligibility,
  CarrierRuleName,
  CarrierRuleOutcome,
} from '../../../../../../api/quotes.api';

/**
 * The three UX states a carrier can be in on the UW dropdown (FPX-7027), derived
 * from the eligibility contract:
 *  - applicable   → every rule passed; freely selectable.
 *  - overridable  → fails only revenue; selectable as a deliberate, audited override.
 *  - unavailable  → fails a hard rule (capacity / adjacency / state / date); shown, not selectable.
 */
export type CarrierStatus = 'applicable' | 'overridable' | 'unavailable';

type EligibilityFlags = {
  readonly applicable: boolean;
  readonly overridable: boolean;
};

export function deriveCarrierStatus(option: EligibilityFlags): CarrierStatus {
  if (option.applicable) {
    return 'applicable';
  }
  if (option.overridable) {
    return 'overridable';
  }
  return 'unavailable';
}

function titleCase(rule: string): string {
  return rule.charAt(0) + rule.slice(1).toLowerCase();
}

export function findSelectedCarrier(
  options: CarrierEligibility[],
  value: string | number
): CarrierEligibility | undefined {
  return options.find((option) => option.treatyId === value);
}

export function findAssignedCarrier(
  options: CarrierEligibility[]
): CarrierEligibility | undefined {
  return options.find((option) => option.isAssigned);
}

/** Whether the currently-selected carrier deviates from the quote's assigned carrier. */
export function isCarrierDeviation(
  options: CarrierEligibility[],
  value: string | number
): boolean {
  const selected = findSelectedCarrier(options, value);
  return selected != null && !selected.isAssigned;
}

/**
 * Whether the currently-selected carrier now fails a hard rule (a recalc pushed it out of
 * applicable and out of overridable) — the trigger for forcing an explicit re-pick (FPX-7040).
 */
export function isSelectedCarrierUnavailable(
  options: CarrierEligibility[],
  value: string | number
): boolean {
  const selected = findSelectedCarrier(options, value);
  return selected != null && !selected.applicable && !selected.overridable;
}

/** Whether some carrier other than the selected one can still be picked (applicable or overridable). */
export function hasSelectableAlternative(
  options: CarrierEligibility[],
  value: string | number
): boolean {
  return options.some(
    (option) =>
      option.treatyId !== value && (option.applicable || option.overridable)
  );
}

/** Human label for a rule, e.g. REVENUE → "Revenue". */
export function ruleLabel(rule: string): string {
  return titleCase(rule);
}

const MONETARY_RULES = new Set<CarrierRuleName>(['REVENUE', 'LIMIT']);

/** Rules whose reason is a dollar figure — flagged with a `$` marker and currency-formatted. */
export function isMonetaryRule(rule: CarrierRuleName): boolean {
  return MONETARY_RULES.has(rule);
}

/**
 * Formats the raw numbers in a monetary rule's reason as USD, so
 * "Revenue 2.51E8 is outside this carrier's band (0.00–250000000.00)" reads
 * "Revenue $251,000,000 is outside this carrier's band ($0–$250,000,000)".
 */
export function formatMonetaryReason(reason: string): string {
  return reason.replace(/\d+(?:\.\d+)?(?:e[+-]?\d+)?/gi, (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) {
      return value;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  });
}

/** How many of a carrier's rules passed. */
export function countPassingRules(rules: CarrierRuleOutcome[]): number {
  return rules.filter((rule) => rule.outcome === 'PASS').length;
}

/** A carrier can be picked when it is applicable or an overridable deviation. */
export function isCarrierSelectable(option: EligibilityFlags): boolean {
  return option.applicable || option.overridable;
}

/** Short status chip label shown on each carrier row. */
export const CARRIER_STATUS_LABEL: Record<CarrierStatus, string> = {
  applicable: 'Eligible',
  overridable: 'Fails — Override available',
  unavailable: 'Fails — Cannot override',
};

export interface CarrierWarning {
  readonly testId: string;
  readonly color: string;
  readonly label: string;
}

const BLOCKED_COLOR = '#e11d48';
const OVERRIDE_COLOR = '#d97706';

/**
 * The at-a-glance warning shown beside the carrier control (shared by the Mantine
 * and MUI selectors). A hard-blocked selection demands an explicit re-pick
 * (FPX-7040); a non-assigned but selectable carrier is an override that needs a
 * reason at release (FPX-7039). An applicable, assigned carrier warns nothing —
 * only a deviation from the assigned carrier is treated as an override.
 */
export function deriveCarrierWarning(
  status: CarrierStatus | undefined,
  isDeviation: boolean
): CarrierWarning | undefined {
  if (status === 'unavailable') {
    return {
      testId: 'carrier-repick-warning',
      color: BLOCKED_COLOR,
      label:
        'This carrier no longer meets the eligibility rules. Choose an available carrier before releasing.',
    };
  }
  if (!isDeviation) {
    return undefined;
  }
  if (status === 'overridable') {
    return {
      testId: 'carrier-override-warning',
      color: OVERRIDE_COLOR,
      label:
        'Revenue override — this carrier is outside its revenue band. A reason is required to release.',
    };
  }
  return {
    testId: 'carrier-override-warning',
    color: OVERRIDE_COLOR,
    label:
      'Carrier override — this differs from the assigned carrier. A reason is required to release.',
  };
}
